import { AppButton, AppTag, AppTextField } from '@/components/atoms';
import { MassagerBasicInfoForm } from '@/components/organisms/admin/massager/MassagerBasicInfoForm';
import { MassagerClinicLocationForm } from '@/components/organisms/admin/massager/MassagerClinicLocationForm';
import { MassagerClinicSelect } from '@/components/organisms/admin/massager/MassagerClinicSelect';
import { MassagerInsurerTable } from '@/components/organisms/admin/massager/MassagerInsurerTable';
import { MassagerLicenseForm } from '@/components/organisms/admin/massager/MassagerLicenseForm';
import { type CreateMassagerFormData } from '@/schemas/createMassagerSchema';
import type { ClinicLocation } from '@/types/admin-clinic-detail';
import { Col, Row } from 'antd';
import React, { useCallback, useRef } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { normalizeLocationId } from '@/utils/locationId';
import { useCreateMassagerFormLogic, type ClinicServiceType } from './useCreateMassagerFormLogic';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';

export type { CreateMassagerFormData };

interface ClinicOption {
  id: number;
  clinic_number: string;
}

interface CreateMassagerFormProps {
  clinicOptions: ClinicOption[];
  clinicLocationOptions: ClinicLocation[];
  initialClinicId?: number | null;
  fixedClinicId?: number | null;
  initialValues?: Partial<CreateMassagerFormData>;
  isEdit?: boolean;
  /** 施術師番号 — read-only, shown only in edit mode (RA2: 施術師番号). */
  serialNumber?: number | null;
  /** A2: はり/きゅう + acu_moxi dates. R2: 免許番号 + massage dates. From clinic.service_type when creating. */
  clinicServiceType?: ClinicServiceType | null;
  siteType?: string | null;
  /** Last visit date (Y-m-d) for the massager. Used for available_to validation in edit mode. */
  lastVisitDate?: string | null;
  onClinicIdChange?: (clinicId: number) => void;
  onSubmit?: (data: CreateMassagerFormData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

const CreateMassagerForm: React.FC<CreateMassagerFormProps> = ({
  clinicOptions,
  clinicLocationOptions,
  initialClinicId,
  fixedClinicId,
  initialValues,
  isEdit = false,
  serialNumber = null,
  clinicServiceType = SERVICE_TYPE_A2,
  siteType,
  lastVisitDate,
  onClinicIdChange,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const effectiveFixedClinicId =
    isEdit && initialValues?.clinic_id != null ? initialValues.clinic_id : fixedClinicId;
  /** Clinic A2 only → disable R2 column. Clinic R2 only → disable A2 column. */
  const disableA2Column = clinicServiceType === SERVICE_TYPE_R2;
  const disableR2Column = clinicServiceType === SERVICE_TYPE_A2;
  const isUpSystemSite = (siteType ?? '').toLowerCase() === 'upsystem';

  const {
    methods: {
      formState: { errors },
      setValue,
    },
    control,
    handleSubmit,
    prefectureFilter,
    setPrefectureFilter,
    massagePrefectureFilter,
    setMassagePrefectureFilter,
    rowMatchesFilter,
    insurerSortField,
    setInsurerSortField,
    insurerSortDir,
    setInsurerSortDir,
    displayOrder,
    isSubmitted,
  } = useCreateMassagerFormLogic({
    clinicOptions,
    clinicLocationOptions,
    initialClinicId,
    fixedClinicId: effectiveFixedClinicId,
    clinicServiceType: clinicServiceType ?? SERVICE_TYPE_A2,
    isUpSystemSite,
    initialValues,
    lastVisitDate,
    onClinicIdChange,
    onSubmit,
  });

  const fieldsA2 = useFieldArray({ control, name: 'clinic_locations_a2' });
  const fieldsR2 = useFieldArray({ control, name: 'clinic_locations_r2' });
  const watchedA2 = useWatch({ control, name: 'clinic_locations_a2', defaultValue: [] });
  const watchedR2 = useWatch({ control, name: 'clinic_locations_r2', defaultValue: [] });
  const watchedAcLicense = useWatch({
    control,
    name: 'acupuncture_license_number',
    defaultValue: '',
  });
  const watchedMoLicense = useWatch({
    control,
    name: 'moxibustion_license_number',
    defaultValue: '',
  });
  const watchedMassageLicense = useWatch({
    control,
    name: 'license_number',
    defaultValue: '',
  });
  const hasA2License =
    (watchedAcLicense ?? '').toString().trim() !== '' ||
    (watchedMoLicense ?? '').toString().trim() !== '';
  const hasR2License = (watchedMassageLicense ?? '').toString().trim() !== '';

  const expandedA2Ref = useRef(new Set<number>());
  const handleA2ExpandChange = useCallback((index: number, isExpanded: boolean) => {
    const s = expandedA2Ref.current;
    if (isExpanded) s.add(index);
    else s.delete(index);
  }, []);

  const expandedR2Ref = useRef(new Set<number>());
  const handleR2ExpandChange = useCallback((index: number, isExpanded: boolean) => {
    const s = expandedR2Ref.current;
    if (isExpanded) s.add(index);
    else s.delete(index);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      {/* Prevent browser/password-manager autofill on this screen */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
      />
      <input
        type="password"
        name="password"
        autoComplete="current-password"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
      />
      {/* 1. Clinic Selection + 1b. 施術師番号 */}
      <Row gutter={[16, 16]}>
        <Col span={isEdit && serialNumber != null ? 12 : 24}>
          <MassagerClinicSelect
            control={control}
            errors={errors}
            clinicOptions={clinicOptions}
            fixedClinicId={effectiveFixedClinicId}
          />
        </Col>
        {isEdit && serialNumber != null && (
          <Col span={12}>
            <label className="mb-1 block font-medium">施術師番号</label>
            <p className="mt-2">{serialNumber}</p>
          </Col>
        )}
      </Row>

      {/* 2. Basic Info (Name, Login, etc.) */}
      <MassagerBasicInfoForm control={control} errors={errors} setValue={setValue} />

      {/* 3. Validity Periods & Dates — 常に2カラム表示。A2のみ時はR2をdisabled、R2のみ時はA2をdisabled */}
      <Row gutter={[16, 16]}>
        {/* Left: はり・きゅう (A2) */}
        <Col span={12} className="min-w-0">
          <fieldset
            disabled={disableA2Column}
            className={`min-w-0 rounded border border-gray-200 p-3 ${disableA2Column ? 'pointer-events-none bg-gray-100 opacity-75' : ''}`}
          >
            <div className="mb-3 flex">
              <div className="w-1/2 rounded-md border border-gray-300 bg-[#F9FAFC] px-3 py-2 text-center text-base font-semibold text-gray-800">
                はり・きゅう師
              </div>
            </div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <MassagerLicenseForm
                  control={control}
                  errors={errors}
                  mode="A2"
                  fullWidthEqualHeight
                  disabled={disableA2Column}
                />
              </Col>
            </Row>

            {/* 拠点選択（はり・きゅう） — 保険者別番号の下 */}
            <div className="mt-4 space-y-3">
              <div className="max-h-[600px] overflow-y-auto pr-1">
                {fieldsA2.fields.map((field, index) => {
                  const rowsA2 = Array.isArray(watchedA2) ? watchedA2 : [];
                  const currentId = normalizeLocationId(rowsA2[index]?.clinic_location_id);
                  const usedIds = rowsA2
                    .map((row, i) =>
                      i === index ? 0 : normalizeLocationId(row?.clinic_location_id)
                    )
                    .filter((id): id is number => Number.isFinite(id) && id > 0);

                  return (
                    <div key={field.id} className="flex items-start gap-2 mb-3 last:mb-0">
                      <div className="min-w-0 flex-1">
                        <MassagerClinicLocationForm
                          control={control}
                          errors={errors}
                          clinicLocationOptions={clinicLocationOptions.filter(loc => {
                            const st = (loc.service_type ?? '').toUpperCase();
                            if (!st || st === SERVICE_TYPE_BOTH) return true;
                            return st === SERVICE_TYPE_A2;
                          })}
                          selectedLocation={clinicLocationOptions.find(
                            loc => normalizeLocationId(loc.id) === currentId
                          )}
                          fieldName={`clinic_locations_a2.${index}.clinic_location_id`}
                          errorMessage={
                            isSubmitted
                              ? (errors?.clinic_locations_a2?.[index]?.clinic_location_id
                                  ?.message ??
                                (index === 0 ? errors?.clinic_locations_a2?.message : undefined))
                              : undefined
                          }
                          forceExpanded={
                            isSubmitted && index === 0 && !!errors?.clinic_locations_a2?.message
                          }
                          disabled={disableA2Column || !hasA2License}
                          usedLocationIds={usedIds}
                          onExpandedChange={exp => handleA2ExpandChange(index, exp)}
                          autoExpandOnMount={currentId === 0}
                          isUpSystemSite={isUpSystemSite}
                        />
                      </div>
                      <AppButton
                        htmlType="button"
                        type="default"
                        danger
                        onClick={() => {
                          expandedA2Ref.current.delete(index);
                          fieldsA2.remove(index);
                        }}
                        className="mt-3 shrink-0"
                        aria-label="この拠点を削除"
                        disabled={disableA2Column || !hasA2License || fieldsA2.fields.length === 1}
                      >
                        削除
                      </AppButton>
                    </div>
                  );
                })}
              </div>
              <AppButton
                htmlType="button"
                type="default"
                onClick={() =>
                  fieldsA2.append({
                    clinic_location_id: 0,
                    available_from: null,
                    available_to: null,
                    regist_date: null,
                    stop_request_date: null,
                  })
                }
                className="w-full sm:w-auto"
                disabled={disableA2Column || !hasA2License}
              >
                追加
              </AppButton>

              <div className="mt-4 min-w-0 border-t border-gray-200 pt-4">
                <MassagerInsurerTable
                  control={control}
                  errors={errors}
                  rowsName="wide_area_union_rows"
                  blockTitle="はり・きゅう"
                  prefectureFilter={prefectureFilter}
                  setPrefectureFilter={setPrefectureFilter}
                  rowMatchesFilter={rowMatchesFilter}
                  insurerSortField={insurerSortField}
                  setInsurerSortField={setInsurerSortField}
                  insurerSortDir={insurerSortDir}
                  setInsurerSortDir={setInsurerSortDir}
                  displayOrder={displayOrder}
                  isEdit={isEdit || Boolean(initialValues)}
                  disabled={disableA2Column || !hasA2License}
                />
              </div>
            </div>
          </fieldset>
        </Col>
        {/* Right: マッサージ (R2) */}
        <Col span={12} className="min-w-0">
          <fieldset
            disabled={disableR2Column}
            className={`min-w-0 rounded border border-gray-200 p-3 ${disableR2Column ? 'pointer-events-none bg-gray-100 opacity-75' : ''}`}
          >
            <div className="mb-3 flex">
              <div className="w-1/2 rounded-md border border-gray-300 bg-[#F9FAFC] px-3 py-2 text-center text-base font-semibold text-gray-800">
                マッサージ師
              </div>
            </div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div className="min-h-[200px] w-full rounded border-2 border-gray-300 p-3">
                  <span className="font-medium">
                    あん摩マッサージ指圧師免許
                    <AppTag color="magenta" className="ml-1">
                      必須
                    </AppTag>
                  </span>
                  <div className="mt-2">
                    <Controller
                      name="license_number"
                      control={control}
                      render={({ field }) => (
                        <AppTextField
                          {...field}
                          value={field.value ?? ''}
                          status={errors.license_number ? 'error' : undefined}
                          className="w-full !h-[39px]"
                          disabled={disableR2Column}
                        />
                      )}
                    />
                  </div>
                  {errors.license_number && (
                    <p className="mt-1 text-sm text-red-500">{errors.license_number.message}</p>
                  )}
                </div>
              </Col>
            </Row>
            {/* 拠点選択（マッサージ） — 保険者別番号の下 */}
            <div className="mt-4 space-y-3">
              <div className="max-h-[600px] overflow-y-auto pr-1">
                {fieldsR2.fields.map((field, index) => {
                  const rowsR2 = Array.isArray(watchedR2) ? watchedR2 : [];
                  const currentId = normalizeLocationId(rowsR2[index]?.clinic_location_id);
                  const usedIds = rowsR2
                    .map((row, i) =>
                      i === index ? 0 : normalizeLocationId(row?.clinic_location_id)
                    )
                    .filter((id): id is number => Number.isFinite(id) && id > 0);

                  return (
                    <div key={field.id} className="flex items-start gap-2 mb-3 last:mb-0">
                      <div className="min-w-0 flex-1">
                        <MassagerClinicLocationForm
                          control={control}
                          errors={errors}
                          clinicLocationOptions={clinicLocationOptions.filter(loc => {
                            const st = (loc.service_type ?? '').toUpperCase();
                            if (!st || st === SERVICE_TYPE_BOTH) return true;
                            return st === SERVICE_TYPE_R2;
                          })}
                          selectedLocation={clinicLocationOptions.find(
                            loc => normalizeLocationId(loc.id) === currentId
                          )}
                          fieldName={`clinic_locations_r2.${index}.clinic_location_id`}
                          errorMessage={
                            isSubmitted
                              ? (errors?.clinic_locations_r2?.[index]?.clinic_location_id
                                  ?.message ??
                                (index === 0 ? errors?.clinic_locations_r2?.message : undefined))
                              : undefined
                          }
                          forceExpanded={
                            isSubmitted && index === 0 && !!errors?.clinic_locations_r2?.message
                          }
                          disabled={disableR2Column || !hasR2License}
                          usedLocationIds={usedIds}
                          onExpandedChange={exp => handleR2ExpandChange(index, exp)}
                          autoExpandOnMount={currentId === 0}
                          isUpSystemSite={isUpSystemSite}
                        />
                      </div>
                      <AppButton
                        htmlType="button"
                        type="default"
                        danger
                        onClick={() => {
                          expandedR2Ref.current.delete(index);
                          fieldsR2.remove(index);
                        }}
                        className="mt-3 shrink-0"
                        aria-label="この拠点を削除"
                        disabled={disableR2Column || !hasR2License || fieldsR2.fields.length === 1}
                      >
                        削除
                      </AppButton>
                    </div>
                  );
                })}
              </div>
              <AppButton
                htmlType="button"
                type="default"
                onClick={() =>
                  fieldsR2.append({
                    clinic_location_id: 0,
                    available_from: null,
                    available_to: null,
                    regist_date: null,
                    stop_request_date: null,
                  })
                }
                className="w-full sm:w-auto"
                disabled={disableR2Column || !hasR2License}
              >
                追加
              </AppButton>
            </div>
            <div className="mt-4 min-w-0 border-t border-gray-200 pt-4">
              <MassagerInsurerTable
                control={control}
                errors={errors}
                rowsName="massage_wide_area_union_rows"
                blockTitle="マッサージ"
                prefectureFilter={massagePrefectureFilter}
                setPrefectureFilter={setMassagePrefectureFilter}
                isEdit={isEdit || Boolean(initialValues)}
                disabled={disableR2Column || !hasR2License}
              />
            </div>
          </fieldset>
        </Col>
      </Row>

      <div className="mt-6 flex gap-2">
        <AppButton type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          更新
        </AppButton>
        {onCancel && (
          <AppButton type="default" onClick={onCancel}>
            詳細画面へ戻る
          </AppButton>
        )}
      </div>
    </form>
  );
};
export default CreateMassagerForm;
